import { useFormSchema } from "src/api/hooks/useFormSchema";
import { UiSchema } from "@rjsf/utils";
import colorsJson from "../../../colors.json";
import { useMemo } from "react";

const baseThemeColors = colorsJson as {
  PrimaryBlack: string;
  PrimaryGray: string;
  AudienceGrey2: string;
  AudienceYellow1: string;
  PrimaryRed: string;
  PrimaryGreen: string;
  SkaneGreen: string;
  White: string;
  Grey1: string;
  Grey2: string;
  Grey4: string;
  Grey7: string;
  Grey9: string;
};

// Helper function to convert hex color to rgba
function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function ThemeStyle({ uiSchema }: { uiSchema: UiSchema }) {
  const { data: schema } = useFormSchema();
  const { colors, fonts } = schema?.theme || {
    colors: {
      primary: baseThemeColors.PrimaryGreen,
      secondary: baseThemeColors.SkaneGreen,
      headerBackgroundColor: baseThemeColors.White,
      headerTextColor: baseThemeColors.Grey9,
    },
    fonts: {
      text: { family: "Arial, sans-serif", size: "16px" },
    },
  };

  const style = useMemo(() => {
    const {
      primary,
      secondary,
      backgroundColor,
      headerTextColor,
      headerBackgroundColor,
    } = colors || {
      primary: baseThemeColors.PrimaryGreen,
      secondary: baseThemeColors.SkaneGreen,
    };
    const focusShadowColor = hexToRgba(primary, 0.15);
    return `

    header {
        background-color: ${colors.headerBackgroundColor} !important;
        color: ${colors.headerTextColor} !important;
    }
      /* Theme-based widget styles */
      .container:focus-within {
        border-color: ${primary} !important;
      } 
        /* Label for root title*/ 
      .field-label {
        color: ${primary ? primary : baseThemeColors.PrimaryGreen} !important;
        font-family: ${
          fonts?.text?.family ? fonts.text.family : "Arial, sans-serif"
        } !important;
        font-size: ${fonts?.text?.size ? fonts.text.size : "16px"} !important;
      }
        /* Inputs */
        input {
          color: ${baseThemeColors.Grey9} !important;
          background-color: ${baseThemeColors.White} !important;
          font-family: ${
            fonts?.text?.family ? fonts.text.family : "Roboto"
          } !important;
          font-size: ${fonts?.text?.size ? fonts.text.size : "16px"} !important;
          border-color: ${baseThemeColors.Grey4} !important;
          border-width: 1px !important;
          border-style: solid !important;
          border-radius: 8px !important; 
          padding: 10px 12px !important;
          transition: all 0.2s ease-in-out !important;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
          width: 100% !important;
          box-sizing: border-box !important;
        }
        input:hover {
          border-color: ${secondary} !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08) !important;
        }
        input:active, input:focus {
          border-color: ${secondary} !important;
          border-width: 2px !important;
          border-style: solid !important;
          border-radius: 8px !important; 
          padding: 9px 11px !important;
          outline: none !important;
          box-shadow: 0 0 0 3px ${focusShadowColor}, 0 2px 4px rgba(0, 0, 0, 0.1) !important;
        }
        input::placeholder {
          color: ${baseThemeColors.Grey7} !important;
          opacity: 0.7 !important;
        }
        textarea {
          color: ${baseThemeColors.Grey9} !important;
          background-color: ${baseThemeColors.White} !important;
          font-family: ${
            fonts?.text?.family ? fonts.text.family : "Roboto"
          } !important;
          font-size: ${fonts?.text?.size ? fonts.text.size : "16px"} !important;
          border-color: ${baseThemeColors.Grey4} !important;
          border-width: 1px !important;
          border-style: solid !important;
          border-radius: 8px !important; 
          padding: 10px 12px !important;
          transition: all 0.2s ease-in-out !important;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
          width: 100% !important;
          box-sizing: border-box !important;
          resize: vertical !important;
          min-height: 100px !important;
          line-height: 1.5 !important;
        }
        textarea:hover {
          border-color: ${secondary} !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08) !important;
        }
        textarea:active, textarea:focus {
          border-color: ${secondary} !important;
          border-width: 2px !important;
          border-style: solid !important;
          border-radius: 8px !important; 
          padding: 9px 11px !important;
          outline: none !important;
          box-shadow: 0 0 0 3px ${focusShadowColor}, 0 2px 4px rgba(0, 0, 0, 0.1) !important;
        }
        textarea::placeholder {
          color: ${baseThemeColors.Grey7} !important;
          opacity: 0.7 !important;
        }
        #root-template {
          overflow-y: auto !important;
          overflow-x: hidden !important;
          height: 100% !important;
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        form {
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
          overflow-x: hidden !important;
        }
        .container {
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        * {
          box-sizing: border-box !important;
        }
        .field-label {
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        select {
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        .object-title {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          color: ${primary} !important;
        }
          .fading-border {
              position: relative;
              width: 100%;
              height: 8px; /* container height */
            }

            .fading-border::before {
              content: "";
              position: absolute;
              left: 0;
              right: 0;
              top: 50%;
              transform: translateY(-50%);

              height: 3px; /* max thickness in center */
              background: ${primary};

              /* Fade to sides */
              mask-image: linear-gradient(
                to right,
                transparent 0%,
                rgba(0, 0, 0, 0.2) 2%,
                black 50%,
                rgba(0, 0, 0, 0.2) 98%,
                transparent 100%
              );

              /* Thickness curve */
              clip-path: polygon(
                0% 50%,
                15% 45%,
                40% 0%,
                60% 0%,
                85% 45%,
                100% 50%,
                85% 55%,
                60% 100%,
                40% 100%,
                15% 55%
              );
            }

            /* Description toggle animation */
            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: translateY(-4px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }

            .description-content {
              animation: fadeIn 0.3s ease-in;
            }

            .description-toggle-btn:focus {
              outline: 2px solid ${primary};
              outline-offset: 2px;
            }
            /* Theme-based class names */
      .secondary-text {
        color: ${secondary} !important;
      }
      .primary-text {
        color: ${primary} !important;
      }

      ${Object.entries(uiSchema).map(([key, value]) => {
        return `
          .${key} {
            ${value}
          }
        `;
      })}
    `;
  }, [uiSchema, colors, fonts]);
  return <style dangerouslySetInnerHTML={{ __html: style }} />;
}
